import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, TrendingUp, Users, MessageSquare, Search, CheckCircle2, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export const AblaufUebersicht = () => {
  const phasen = [
    { nummer: '01', title: 'Unterlagen & Vorbereitung', dauer: '2-4 Wochen', icon: FileText, link: '/ablauf/phase-1-unterlagen', beschreibung: 'Zusammenstellung aller Dokumente und Erstanalyse' },
    { nummer: '02', title: 'Bewertung & Positionierung', dauer: '1-2 Wochen', icon: TrendingUp, link: '/ablauf/phase-2-bewertung', beschreibung: 'Professionelle Marktanalyse und Preisstrategie' },
    { nummer: '03', title: 'Käuferansprache & Vermarktung', dauer: '8-16 Wochen', icon: Users, link: '/ablauf/phase-3-vermarktung', beschreibung: 'Gezielte Ansprache passender Investoren' },
    { nummer: '04', title: 'Verhandlung & Due Diligence', dauer: '4-8 Wochen', icon: MessageSquare, link: '/ablauf/phase-4-verhandlung', beschreibung: 'Angebotsprüfung und Käuferprüfung begleiten' },
    { nummer: '05', title: 'Notartermin & Kaufvertrag', dauer: '2-4 Wochen', icon: Search, link: '/ablauf/phase-5-notar', beschreibung: 'Vertragsabstimmung und notarielle Beurkundung' },
    { nummer: '06', title: 'Übergabe & After Sale', dauer: '4-8 Wochen', icon: CheckCircle2, link: '/ablauf/phase-6-uebergabe', beschreibung: 'Kaufpreiszahlung und Übergabe an Käufer' }
  ];

  const gesamtdauer = '6-12 Monate';

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Ablauf Verkauf Handelsimmobilie – 6 Phasen zum Erfolg"
        description="Strukturierter Verkaufsprozess für Handelsimmobilien in 6 Phasen. Von Unterlagen bis Übergabe mit klaren Zeitrahmen und Verantwortlichkeiten."
        url="/ablauf"
      />
      <Navbar />
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-blue-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
              So läuft der Verkauf ab
            </h1>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto mb-8" style={{ color: 'var(--gray-700)' }}>
              Unser strukturierter 6-Phasen-Prozess für den erfolgreichen Verkauf Ihrer Handelsimmobilie
            </p>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-100 rounded-full">
              <Clock className="h-6 w-6" style={{ color: 'var(--navy-600)' }} />
              <span className="text-lg font-semibold" style={{ color: 'var(--navy-800)' }}>Gesamtdauer: {gesamtdauer}</span>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {phasen.map((phase, index) => {
                const IconComponent = phase.icon;
                return (
                  <Link key={index} to={phase.link}>
                    <Card className="glass-card p-8 hover:shadow-2xl transition-all duration-300 border-l-4" style={{ borderLeftColor: 'var(--gold-600)' }}>
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-center">
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: index % 2 === 0 ? 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' : 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
                            <span className="text-3xl font-bold text-white">{phase.nummer}</span>
                          </div>
                          <div className="lg:hidden">
                            <h3 className="text-2xl font-bold" style={{ color: 'var(--navy-800)' }}>{phase.title}</h3>
                          </div>
                        </div>

                        <div className="hidden lg:block lg:col-span-2">
                          <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--navy-800)' }}>{phase.title}</h3>
                          <p style={{ color: 'var(--gray-700)' }}>{phase.beschreibung}</p>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                            <span className="font-semibold" style={{ color: 'var(--gray-700)' }}>{phase.dauer}</span>
                          </div>
                          <ArrowRight className="h-6 w-6" style={{ color: 'var(--navy-600)' }} />
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10">
              <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Wichtige Hinweise</h2>
              <ul className="space-y-4" style={{ color: 'var(--gray-700)' }}>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                  <span><strong>Zeitrahmen sind Richtwerte:</strong> Die tatsächliche Dauer hängt von Objektkomplexität, Marktlage und Käuferanzahl ab.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                  <span><strong>Parallelität möglich:</strong> Einige Phasen überlappen sich (z.B. Unterlagenvervollständigung während Vermarktung).</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                  <span><strong>Gute Vorbereitung spart Zeit:</strong> Vollständige Unterlagen ab Phase 1 können die Gesamtdauer um 2-4 Monate verkürzen.</span>
                </li>
              </ul>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Bereit zu starten?</h2>
            <p className="text-lg mb-8 opacity-90">Vereinbaren Sie ein unverbindliches Erstgespräch und wir erklären Ihnen den Prozess im Detail.</p>
            <Link to="/ersteinschaetzung">
              <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                Kostenlose Ersteinschätzung
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AblaufUebersicht;